import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "../axiosConfig";
import Table from "../components/Table";
import Modal from "../components/Modal";
import "./SlideManager.css";
import "./Import.css";
import { typeIcons, IconFlag, IconFlagPlus, IconFlagMinus } from "../components/Utils";
import { TbFlagPlus } from "react-icons/tb";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { useTaskRefresh } from "../context/TaskRefreshContext.jsx";
import { searchAndRankSlides } from "../utils/searchUtils";
import { placePopover } from "../components/popoverPosition";
import Checkbox from '../components/Checkbox';
import { parseDbDate } from "../utils/dates";
import PageTitle from "../components/PageTitle";

export default function SlideManager() {
  const refreshTasks = useTaskRefresh();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageDiscrepancies, setImageDiscrepancies] = useState([]);

  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const isAdministrator =
    !permissionsLoading && Boolean(permissions?.is_administrator);

  // Slide import state
  const [importSlidesModalOpen, setImportSlidesModalOpen] = useState(false);
  const [importSlidesMode, setImportSlidesMode] = useState("upload"); // 'upload' | 'restore'
  const [importSlidesFile, setImportSlidesFile] = useState(null);
  const [importSlidesUploading, setImportSlidesUploading] = useState(false);
  const [importSlidesResponse, setImportSlidesResponse] = useState(null);
  const [importSlidesError, setImportSlidesError] = useState(null);
  const [importSlidesDragActive, setImportSlidesDragActive] = useState(false);
  const [importSlidesInventory, setImportSlidesInventory] = useState([]);
  const [importSlidesSortFiles, setImportSlidesSortFiles] = useState([]);
  const [importSlidesSortNumbersByFile, setImportSlidesSortNumbersByFile] =
    useState({});
  const [importSlidesSortTitlesByFile, setImportSlidesSortTitlesByFile] =
    useState({});
  const [importSlidesConfirming, setImportSlidesConfirming] = useState(false);
  const [importSlidesConfirmError, setImportSlidesConfirmError] =
    useState(null);
  const [importSlidesConfirmResponse, setImportSlidesConfirmResponse] =
    useState(null);
  const [importSlidesFinalConfirmOpen, setImportSlidesFinalConfirmOpen] =
    useState(false);
  const [importSlidesApplying, setImportSlidesApplying] = useState(false);
  const [importSlidesApplyingCount, setImportSlidesApplyingCount] =
    useState(0);
  // Bumped on each import confirm and when the user dismisses the "Importing..."
  // modal; a wait loop whose run id no longer matches stops (no edit modals open).
  const importWaitRunRef = useRef(0);

  // After an import is applied (async task), we optionally open the edit modal
  // sequentially for each affected destination slide.
  const autoEditQueueRef = useRef([]);
  const autoEditActiveRef = useRef(false);
  const autoEditAdvanceRef = useRef(null);

  const availableFlagsLoadInFlightRef = useRef(false);

  const editFlagsDropdownRef = useRef(null);
  const editFlagsAnchorElRef = useRef(null);

  // Import slide image generation countdown/loading UI
  const [importSlidesImagesReady, setImportSlidesImagesReady] = useState(false);
  const [importSlidesImageBaseName, setImportSlidesImageBaseName] =
    useState("");
  const [importSlidesImageWaitMs, setImportSlidesImageWaitMs] = useState(0);
  const [importSlidesImageWaitUntil, setImportSlidesImageWaitUntil] =
    useState(null);
  const [importSlidesImageProgress, setImportSlidesImageProgress] = useState(0);
  // Imported slide thumbnail retry state (keyed by imported slide_number)
  const [
    importSlidesImageRetryNonceBySlideNo,
    setImportSlidesImageRetryNonceBySlideNo,
  ] = useState({});
  const [
    importSlidesImageRetryCountBySlideNo,
    setImportSlidesImageRetryCountBySlideNo,
  ] = useState({});
  const [
    importSlidesImageLoadedBySlideNo,
    setImportSlidesImageLoadedBySlideNo,
  ] = useState({});
  const importSlidesImageRetryTimeoutsRef = useRef({});
  const MAX_IMPORT_IMAGE_RETRIES = 30;

  // Import slides modal: hover-to-zoom should enlarge BOTH previews in the row.
  const [importSlidesHoverZoomKey, setImportSlidesHoverZoomKey] =
    useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", flags: [] });
  // Values the edit modal was opened with, so save only sends fields the user
  // actually changed (never writes back a possibly-stale copy of the rest).
  const editInitialRef = useRef({ name: "", flagsKey: "" });
  const [slideDetail, setSlideDetail] = useState(null);
  const [confirmingReviewed, setConfirmingReviewed] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingSlide, setDeletingSlide] = useState(false);
  const [deleteSlideError, setDeleteSlideError] = useState(null);

  // Delete modal: optional "replace" target selection.
  const [replaceFile, setReplaceFile] = useState("Admin.pptx");
  const [replaceSlideId, setReplaceSlideId] = useState("");
  const [replacingSlide, setReplacingSlide] = useState(false);
  const replaceInitRef = useRef(false);
  const [availableFlags, setAvailableFlags] = useState([]);
  const [editFlagsWindowOpen, setEditFlagsWindowOpen] = useState(false);
  const [editFlagsDropdownGroupsOpen, setEditFlagsDropdownGroupsOpen] =
    useState({});
  const [editFlagsDropdownPos, setEditFlagsDropdownPos] = useState(null);

  // Flags management state
  const [flagsModalOpen, setFlagsModalOpen] = useState(false);
  const [flags, setFlags] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [editingFlagId, setEditingFlagId] = useState(null);
  const [editingFlagName, setEditingFlagName] = useState("");
  const [creatingNewFlag, setCreatingNewFlag] = useState(false);
  const [newFlagName, setNewFlagName] = useState("");

  // Bulk operations state
  const [selectedSlides, setSelectedSlides] = useState(new Set());
  // Keep a stable, filter-independent order of selection for exporting.
  const [selectedSlideOrder, setSelectedSlideOrder] = useState([]);
  const [bulkFlagsModalOpen, setBulkFlagsModalOpen] = useState(false);
  const [bulkChanges, setBulkChanges] = useState([]);
  const [bulkChangeCounter, setBulkChangeCounter] = useState(0);
  const [filteredData, setFilteredData] = useState([]);

  // Slide name search (above-table, searches name + flags)
  const [slideSearchTerm, setSlideSearchTerm] = useState("");

  // Bulk flags modal dropdown state (add/remove)
  const bulkAddFlagsDropdownRef = useRef(null);
  const bulkRemoveFlagsDropdownRef = useRef(null);
  const [bulkAddFlagsDropdownOpen, setBulkAddFlagsDropdownOpen] =
    useState(false);
  const [bulkRemoveFlagsDropdownOpen, setBulkRemoveFlagsDropdownOpen] =
    useState(false);
  const [bulkAddFlagsDropdownGroupsOpen, setBulkAddFlagsDropdownGroupsOpen] =
    useState({});
  const [
    bulkRemoveFlagsDropdownGroupsOpen,
    setBulkRemoveFlagsDropdownGroupsOpen,
  ] = useState({});
  const [bulkAddFlagsDropdownPos, setBulkAddFlagsDropdownPos] = useState(null);
  const [bulkRemoveFlagsDropdownPos, setBulkRemoveFlagsDropdownPos] =
    useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const splitPrefixedOption = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    // Group by the first " - " delimiter: "XXX - rest"
    const delimiter = " - ";
    const idx = text.indexOf(delimiter);
    if (idx <= 0) return { prefix: null, suffix: null };
    const prefix = text.slice(0, idx).trim();
    const suffix = text.slice(idx + delimiter.length).trim();
    if (!prefix || !suffix) return { prefix: null, suffix: null };
    return { prefix, suffix };
  };

  const resetImportSlidesState = () => {
    setImportSlidesFile(null);
    setImportSlidesUploading(false);
    setImportSlidesResponse(null);
    setImportSlidesError(null);
    setImportSlidesDragActive(false);
    setImportSlidesInventory([]);
    setImportSlidesSortFiles([]);
    setImportSlidesSortNumbersByFile({});
    setImportSlidesSortTitlesByFile({});
    setImportSlidesConfirming(false);
    setImportSlidesConfirmError(null);
    setImportSlidesConfirmResponse(null);
    setImportSlidesFinalConfirmOpen(false);

    setImportSlidesImagesReady(false);
    setImportSlidesImageBaseName("");
    setImportSlidesImageWaitMs(0);
    setImportSlidesImageWaitUntil(null);
    setImportSlidesImageProgress(0);
    setImportSlidesImageRetryNonceBySlideNo({});
    setImportSlidesImageRetryCountBySlideNo({});
    setImportSlidesImageLoadedBySlideNo({});
    try {
      const timeouts = importSlidesImageRetryTimeoutsRef.current || {};
      Object.values(timeouts).forEach((id) => {
        try {
          window.clearTimeout(id);
        } catch {
          /* ignore */
        }
      });
      importSlidesImageRetryTimeoutsRef.current = {};
    } catch {
      // ignore
    }

    const input = document.getElementById("pptx-file-input");
    if (input) input.value = "";
  };

  const loadAvailableFlagsIfNeeded = async ({ force = false } = {}) => {
    if (availableFlagsLoadInFlightRef.current) return;
    if (!force && Array.isArray(availableFlags) && availableFlags.length > 0)
      return;

    availableFlagsLoadInFlightRef.current = true;
    try {
      const response = await axios.get("/flags/");
      setAvailableFlags(response.data.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load flags",
      );
    } finally {
      availableFlagsLoadInFlightRef.current = false;
    }
  };

  useEffect(() => {
    // Load flags once on page load so opening the edit modal doesn't refetch.
    loadAvailableFlagsIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!importSlidesImageWaitUntil || !importSlidesImageWaitMs) return;

    setImportSlidesImagesReady(false);
    setImportSlidesImageProgress(0);

    let intervalId = null;
    const tick = () => {
      const now = Date.now();
      const remaining = importSlidesImageWaitUntil - now;
      if (remaining <= 0) {
        setImportSlidesImageProgress(1);
        setImportSlidesImagesReady(true);
        if (intervalId) window.clearInterval(intervalId);
        return;
      }
      const p = 1 - remaining / importSlidesImageWaitMs;
      const clamped = Math.max(0, Math.min(1, p));
      setImportSlidesImageProgress(clamped);
    };

    tick();
    intervalId = window.setInterval(tick, 100);
    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [importSlidesImageWaitUntil, importSlidesImageWaitMs]);

  const handleOpenImportSlidesModal = () => {
    setImportSlidesMode("upload");
    setImportSlidesModalOpen(true);
    resetImportSlidesState();
  };

  const handleOpenRestoreSlidesModal = () => {
    setImportSlidesMode("restore");
    setImportSlidesModalOpen(true);
    resetImportSlidesState();
    restoreSlidesFromArchive({ force: true });
  };

  const handleCloseImportSlidesModal = () => {
    setImportSlidesModalOpen(false);
    resetImportSlidesState();
    setImportSlidesMode("upload");
  };

  const validateAndSetImportSlidesFile = (file) => {
    setImportSlidesError(null);
    setImportSlidesResponse(null);

    if (!file) return;

    const fileExt = file.name.toLowerCase().split(".").pop();
    if (fileExt !== "pptx") {
      setImportSlidesError("Please select a PPTX file");
      return;
    }

    setImportSlidesFile(file);

    // Requirement: when a valid file is selected, upload automatically.
    // Pass the file directly so we don't depend on state update timing.
    uploadSlidesFile(file);
  };

  const handleImportSlidesFileSelect = (event) => {
    const file = event.target.files[0];
    validateAndSetImportSlidesFile(file);
  };

  const handleImportSlidesDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setImportSlidesDragActive(true);
    } else if (e.type === "dragleave") {
      setImportSlidesDragActive(false);
    }
  };

  const handleImportSlidesDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImportSlidesDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetImportSlidesFile(e.dataTransfer.files[0]);
    }
  };

  const processImportSlidesUploadResponse = useCallback(
    async (responseData) => {
      // Per requirement: refresh slides first so sort dropdowns are based on current DB
      const slidesRes = await axios.get("/slides/", { withCredentials: true });
      const slidesJson = slidesRes.data;
      let slides = [];
      if (Array.isArray(slidesJson)) slides = slidesJson;
      else if (slidesJson && Array.isArray(slidesJson.slides))
        slides = slidesJson.slides;
      else if (slidesJson && Array.isArray(slidesJson.data))
        slides = slidesJson.data;

      const filesSet = new Set();
      const maxByFile = {};
      const titlesByFile = {};
      for (const s of slides) {
        const f = s?.file;
        const n = Number(s?.slide_number);
        if (!f) continue;
        filesSet.add(f);
        if (!Number.isFinite(n)) continue;
        maxByFile[f] = Math.max(maxByFile[f] || 0, n);

        if (!titlesByFile[f]) titlesByFile[f] = {};
        const title = (s?.name || "").toString().trim();
        if (title) titlesByFile[f][n] = title;
      }

      const distinctFiles = Array.from(filesSet).sort((a, b) =>
        String(a).localeCompare(String(b)),
      );
      const numbersByFile = {};
      for (const f of distinctFiles) {
        const max = maxByFile[f] || 0;
        numbersByFile[f] = Array.from({ length: max }, (_, i) => i + 1);
      }

      setImportSlidesSortFiles(distinctFiles);
      setImportSlidesSortNumbersByFile(numbersByFile);
      setImportSlidesSortTitlesByFile(titlesByFile);

      const inventoryRaw = Array.isArray(responseData?.slides)
        ? responseData.slides
        : [];
      // Default: all imported slides start unselected.
      const inventory = inventoryRaw.map((s) => {
        const item = { ...(s || {}), ready: false };

        // Track whether the slide name was last set from the destination title.
        // (Manual edits are allowed, but replace/position changes can overwrite them.)
        item._autoNameFromTarget = false;

        // Requirement: only auto-set the slide name from the selected destination
        // slide title when the operation is overwrite (replace).
        const op = (item?.sort_operation || "").toString().toLowerCase();
        if (op === "replace") {
          const f = item?.sort_file;
          const n = Number(item?.sort_number);
          const title = f && Number.isFinite(n) ? titlesByFile?.[f]?.[n] : null;
          if (title) {
            item.name = title;
            item._autoNameFromTarget = true;
          }
        }
        return item;
      });

      setImportSlidesInventory(inventory);
      setImportSlidesResponse(responseData);
      setImportSlidesConfirmError(null);
      setImportSlidesConfirmResponse(null);

      // After upload: wait for slide image generation, then attempt to load images.
      try {
        const filename = (responseData?.filename || "").toString();
        const baseName = filename.replace(/\.pptx$/i, "");
        const slideCount = Array.isArray(inventory) ? inventory.length : 0;
        const waitSeconds = 3.0 + 0.1 * Number(slideCount || 0);
        const waitMs = Math.max(0, Math.round(waitSeconds * 1000));

        setImportSlidesImagesReady(false);
        setImportSlidesImageBaseName(baseName);
        setImportSlidesImageWaitMs(waitMs);
        setImportSlidesImageWaitUntil(Date.now() + waitMs);
      } catch {
        // If anything goes wrong, just skip the countdown UI.
        setImportSlidesImagesReady(false);
        setImportSlidesImageBaseName("");
        setImportSlidesImageWaitMs(0);
        setImportSlidesImageWaitUntil(null);
        setImportSlidesImageProgress(0);
      }
    },
    [],
  );

  const uploadSlidesFile = async (fileOverride = null) => {
    const fileToUpload = fileOverride || importSlidesFile;
    if (!fileToUpload) {
      setImportSlidesError("Please select a file first");
      return;
    }

    if (importSlidesUploading) return;

    setImportSlidesUploading(true);
    setImportSlidesError(null);
    setImportSlidesResponse(null);

    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);

      const res = await axios.post("/import-slides/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      await processImportSlidesUploadResponse(res.data);
    } catch (err) {
      setImportSlidesError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error uploading PPTX",
      );
    } finally {
      setImportSlidesUploading(false);
    }
  };

  const restoreSlidesFromArchive = async ({ force = false } = {}) => {
    if (importSlidesUploading && !force) return;

    setImportSlidesUploading(true);
    setImportSlidesError(null);
    setImportSlidesResponse(null);

    try {
      // Intentionally send a POST with no file; backend will use DELETED_SLIDES_PATH.
      const emptyForm = new FormData();
      const res = await axios.post("/import-slides/", emptyForm, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      await processImportSlidesUploadResponse(res.data);
    } catch (err) {
      setImportSlidesError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error restoring slides",
      );
    } finally {
      setImportSlidesUploading(false);
    }
  };

  const handleUpdateImportSlide = (index, patch) => {
    setImportSlidesInventory((prev) => {
      const next = [...prev];
      const before = next[index] || {};
      const updated = { ...before, ...patch };

      const patchObj = patch || {};
      const wasReplace =
        (before?.sort_operation || "").toString().toLowerCase() === "replace";
      const isReplace =
        (updated?.sort_operation || "").toString().toLowerCase() === "replace";

      const opChangedToReplace =
        Object.prototype.hasOwnProperty.call(patchObj, "sort_operation") &&
        isReplace &&
        !wasReplace;
      const destinationChangedWhileReplace =
        wasReplace &&
        isReplace &&
        (Object.prototype.hasOwnProperty.call(patchObj, "sort_file") ||
          Object.prototype.hasOwnProperty.call(patchObj, "sort_number"));

      // Requirement: ignore manual edits. If the operation is changed to 'replace',
      // OR if the operation is already 'replace' and the selected destination changes,
      // force the slide name to match the selected destination slide's title.
      if (opChangedToReplace || destinationChangedWhileReplace) {
        const f = updated?.sort_file;
        const n = Number(updated?.sort_number);
        const title =
          f && Number.isFinite(n)
            ? importSlidesSortTitlesByFile?.[f]?.[n]
            : null;
        updated.name = (title || "").toString();
        updated._autoNameFromTarget = Boolean(updated.name);
      }

      // If the sort fields are incomplete, force ready=false
      const hasOp = Boolean(updated?.sort_operation);
      const hasFile = Boolean(updated?.sort_file);
      const hasNumber =
        updated?.sort_number !== null &&
        updated?.sort_number !== undefined &&
        updated?.sort_number !== "";
      const isComplete = hasOp && hasFile && hasNumber;
      if (!isComplete) updated.ready = false;

      next[index] = updated;
      return next;
    });
  };

  const handleToggleImportSlideReady = (index) => {
    setImportSlidesInventory((prev) => {
      const next = [...prev];
      const current = next[index] || {};
      next[index] = { ...current, ready: !Boolean(current.ready) };
      return next;
    });
  };

  const handleConfirmImportedSlides = async (payloadOverride = null) => {
    setImportSlidesConfirming(true);
    setImportSlidesConfirmError(null);
    setImportSlidesConfirmResponse(null);

    try {
      const buildConfirmPayload = () => {
        const readySlides = (
          Array.isArray(importSlidesInventory) ? importSlidesInventory : []
        )
          .filter((s) => s && s.ready === true)
          .map((s) => {
            // Requirement: do not include slide content in confirm payload.
            // Also strip internal-only helper fields.
            // eslint-disable-next-line no-unused-vars
            const { content, _autoNameFromTarget, ...rest } = s;
            return rest;
          });

        return {
          filename: importSlidesResponse?.filename,
          original_filename: importSlidesResponse?.original_filename,
          slides: readySlides,
        };
      };

      const payload = payloadOverride || buildConfirmPayload();

      const res = await axios.post("/import-slides/confirm/", payload, {
        withCredentials: true,
      });
      setImportSlidesConfirmResponse(res.data);
      return res.data?.task_id || true;
    } catch (err) {
      try {
        // eslint-disable-next-line no-console
        console.error("Import slides confirm failed", {
          status: err?.response?.status,
          data: err?.response?.data,
          url: err?.config?.url,
          method: err?.config?.method,
          headers: err?.config?.headers,
        });
      } catch (e) {
        // ignore
      }

      const data = err.response?.data;
      const base = data?.error || data?.message || "Error confirming import";
      const details = data?.details ? ` (${data.details})` : "";
      const msg = `${base}${details}`;
      setImportSlidesConfirmError(msg);
      setError((prev) => prev || msg);
      return false;
    } finally {
      setImportSlidesConfirming(false);
    }
  };

  const importSlidesSelectionStats = useMemo(() => {
    let selectedCount = 0;
    let overwriteCount = 0;
    let addNewCount = 0;

    for (const slide of importSlidesInventory || []) {
      if (!slide || !slide.ready) continue;
      selectedCount += 1;
      const op = (slide.sort_operation || "").toString().toLowerCase();
      if (op === "replace") overwriteCount += 1;
      else if (op === "follow") addNewCount += 1;
    }

    return { selectedCount, overwriteCount, addNewCount };
  }, [importSlidesInventory]);

  const handleOpenImportSlidesFinalConfirm = () => {
    if (importSlidesConfirming) return;
    if (replaceConflictMessage) return;
    if ((importSlidesSelectionStats?.selectedCount || 0) < 1) return;
    setImportSlidesFinalConfirmOpen(true);
  };

  const handleCancelImportSlidesFinalConfirm = () => {
    if (importSlidesConfirming) return;
    setImportSlidesFinalConfirmOpen(false);
  };

  const handleConfirmImportSlidesFinalConfirm = async () => {
    if (importSlidesConfirming) return;

    const payload = {
      filename: importSlidesResponse?.filename,
      original_filename: importSlidesResponse?.original_filename,
      slides: (Array.isArray(importSlidesInventory)
        ? importSlidesInventory
        : []
      )
        .filter((s) => s && s.ready === true)
        .map((s) => {
          // eslint-disable-next-line no-unused-vars
          const { content, _autoNameFromTarget, ...rest } = s;
          return rest;
        }),
    };

    importWaitRunRef.current += 1;
    const runId = importWaitRunRef.current;
    const isStale = () => importWaitRunRef.current !== runId;
    setImportSlidesApplying(true);
    setImportSlidesApplyingCount(payload.slides.length);

    try {
      const deckStemFromFile = (fileName) => {
        try {
          return (fileName || "")
            .toString()
            .trim()
            .replace(/\.pptx$/i, "");
        } catch {
          return "";
        }
      };

      const affectedDeckStems = Array.from(
        new Set(
          payload.slides
            .map((s) => deckStemFromFile(s?.sort_file))
            .filter(Boolean),
        ),
      );

      const clearBrowserSlideImageCacheForDeckStems = async (deckStems) => {
        const stems = Array.isArray(deckStems) ? deckStems.filter(Boolean) : [];
        if (!stems.length) return;
        if (typeof window === "undefined") return;
        if (!("caches" in window)) return;

        try {
          const cacheNames = await window.caches.keys();
          for (const cacheName of cacheNames) {
            let cache;
            try {
              cache = await window.caches.open(cacheName);
            } catch {
              continue;
            }

            let requests = [];
            try {
              requests = await cache.keys();
            } catch {
              requests = [];
            }

            for (const req of requests) {
              try {
                const url = (req?.url || "").toString();
                if (!url.includes("/api/slide-image/")) continue;

                // URL contains /api/slide-image/<tag>/; tag typically starts with '<deckStem>_'
                const match = url.match(/\/api\/slide-image\/([^/]+)\//i);
                const tag = match?.[1] ? decodeURIComponent(match[1]) : "";
                if (!tag) continue;

                const shouldDelete = stems.some((stem) =>
                  tag.toLowerCase().startsWith(`${stem.toLowerCase()}_`),
                );
                if (!shouldDelete) continue;

                await cache.delete(req);
              } catch {
                // ignore per-entry errors
              }
            }
          }
        } catch {
          // ignore cache API errors
        }
      };

      await clearBrowserSlideImageCacheForDeckStems(affectedDeckStems);

      const importDeckBaseName = deckStemFromFile(payload?.filename);

      setImportSlidesFinalConfirmOpen(false);

      // Requirement: after final confirmation, close the import modal.
      // Do not reset import state until after the confirm request uses it.
      setImportSlidesModalOpen(false);

      // Now that we've captured the payload, clear modal state (including retry timers).
      resetImportSlidesState();

      const ok = await handleConfirmImportedSlides(payload);
      if (!ok) return;
      const importTaskId = typeof ok === "string" ? ok : null;
      refreshTasks();

      if (!importTaskId) {
        setError(
          "Import could not be started. Check the task log for details.",
        );
        return;
      }

      const sleep = (ms) =>
        new Promise((resolve) => window.setTimeout(resolve, ms));

      // Wait for the backend task to finish. On completion it records the exact
      // DB rows it wrote (Task.arg.applied), so we never infer them from /slides/.
      const POLL_MS = 1500;
      const MAX_WAIT_MS = 15 * 60 * 1000;
      const startedAt = Date.now();
      let taskRecord = null;
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        if (isStale()) break;
        try {
          const taskRes = await axios.get(
            `/celery-tasks/?task_id=${encodeURIComponent(importTaskId)}`,
          );
          const taskList = taskRes.data?.results || taskRes.data;
          const found = Array.isArray(taskList)
            ? taskList.find((t) => t.task_id === importTaskId)
            : null;
          if (found && found.status === "completed") {
            taskRecord = found;
            break;
          }
        } catch {
          // Non-fatal; keep polling.
        }
        await sleep(POLL_MS);
      }

      if (!taskRecord) {
        if (!isStale()) {
          setError(
            "The import is still running in the background. Check the task panel; slides will update when it finishes.",
          );
        }
        return;
      }

      let applied = [];
      try {
        const parsed = JSON.parse(taskRecord.arg || "{}");
        if (Array.isArray(parsed?.applied)) applied = parsed.applied;
      } catch {
        applied = [];
      }

      // Show the imported preview image for each written row straight away
      // (destination deck images are regenerated asynchronously).
      const importedImageBySlideId = new Map();
      if (importDeckBaseName) {
        for (const a of applied) {
          const id = Number(a?.slide_id);
          const srcNo = Number(a?.src_slide_number);
          if (!Number.isFinite(id) || id <= 0) continue;
          if (!Number.isFinite(srcNo) || srcNo <= 0) continue;
          const tag = `${importDeckBaseName}_${srcNo}`;
          importedImageBySlideId.set(
            id,
            `/api/slide-image/${encodeURIComponent(tag)}/`,
          );
        }
      }

      let slides = [];
      try {
        const response = await axios.get("/slides/");
        const json = response.data;
        if (Array.isArray(json)) slides = json;
        else if (json && Array.isArray(json.slides)) slides = json.slides;
        else if (json && Array.isArray(json.data)) slides = json.data;
      } catch {
        slides = [];
      }

      const patchedSlides = slides.map((s) => {
        const override = importedImageBySlideId.get(Number(s?.id));
        return override ? { ...s, has_image: true, image_url: override } : s;
      });
      if (patchedSlides.length) setData(patchedSlides);

      if (taskRecord.result === "fail") {
        setError("Import failed. Check the task log for details.");
      } else if (!applied.length) {
        setError(
          "Import finished but no slides were updated. Check the task log for details.",
        );
      } else {
        setError(null);
      }

      if (isStale()) return;

      const rowsById = new Map(patchedSlides.map((s) => [Number(s?.id), s]));
      const queueIds = [];
      for (const a of applied) {
        const id = Number(a?.slide_id);
        if (!Number.isFinite(id) || id <= 0) continue;
        if (!rowsById.has(id) || queueIds.includes(id)) continue;
        queueIds.push(id);
      }

      // Open each written slide's edit modal in turn. Each modal is filled from a
      // fresh GET of that slide at the moment it opens, never from a cached row.
      const openAutoEditSlide = async (id) => {
        const row = rowsById.get(id) || { id };
        let detail = null;
        try {
          const res = await axios.get(`/slides/${id}/`, {
            withCredentials: true,
          });
          detail = res.data?.data ?? null;
        } catch {
          detail = null;
        }
        const fresh = detail
          ? {
              ...row,
              name: detail.name,
              flags: detail.flags,
              modified: detail.modified,
              reviewed: detail.reviewed,
              tag: detail.tag,
              file: detail.file,
              slide_number: detail.slide_number,
            }
          : row;
        await openEditModal(fresh, detail);
      };

      const openNextAutoEditIfAny = async () => {
        if (!autoEditActiveRef.current) return;
        const next = autoEditQueueRef.current.shift();
        if (next === undefined) {
          autoEditActiveRef.current = false;
          autoEditQueueRef.current = [];
          return;
        }
        await openAutoEditSlide(next);
      };

      // Stash the "advance" function for save/cancel handlers.
      // (This is reassigned each import; it is a no-op when inactive.)
      autoEditAdvanceRef.current = openNextAutoEditIfAny;

      if (!queueIds.length) return;
      autoEditQueueRef.current = queueIds.slice();
      autoEditActiveRef.current = true;

      setImportSlidesApplying(false);
      await openNextAutoEditIfAny();
    } finally {
      if (!isStale()) {
        setImportSlidesApplying(false);
        setImportSlidesApplyingCount(0);
      }
    }
  };

  const renderCircularProgress = (progress01, size = 28, stroke = 3) => {
    const p = Math.max(0, Math.min(1, Number(progress01 || 0)));
    const angle = Math.round(360 * p);
    const progressLayer =
      p <= 0
        ? "conic-gradient(from -90deg, transparent 0deg 360deg)"
        : `conic-gradient(from -90deg, #3F617D 0deg ${angle}deg, transparent ${angle}deg 360deg)`;
    const baseLayer = "conic-gradient(var(--border-color) 0deg 360deg)";
    const ringMask = `radial-gradient(farthest-side, transparent calc(50% - ${stroke}px), #000 calc(50% - ${stroke}px), #000 50%, transparent 50%)`;

    return (
      <div
        aria-label="Progress"
        role="img"
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          backgroundImage: `${progressLayer}, ${baseLayer}`,
          WebkitMaskImage: ringMask,
          maskImage: ringMask,
        }}
      />
    );
  };

  const replaceConflictMessage = useMemo(() => {
    const seen = new Map();
    for (const slide of importSlidesInventory || []) {
      if (!slide) continue;
      // Only consider selected slides for conflict checks.
      if (slide.ready !== true) continue;
      const op = (slide.sort_operation || "").toString().toLowerCase();
      if (op !== "replace") continue;

      const file = slide.sort_file;
      const sortNumber = slide.sort_number;
      if (
        !file ||
        sortNumber === null ||
        sortNumber === undefined ||
        sortNumber === ""
      )
        continue;

      const key = `${file}::${sortNumber}`;
      if (seen.has(key)) {
        const first = seen.get(key);
        const slideA = first?.slide_number;
        const slideB = slide?.slide_number;
        return `Replace conflict: slide ${slideA} and ${slideB} both set to replace slide ${sortNumber} in ${file}`;
      }

      seen.set(key, slide);
    }
    return null;
  }, [importSlidesInventory]);

  const fetchSlides = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get("/slides/");
      const json = response.data;
      let slides = [];
      if (Array.isArray(json)) slides = json;
      else if (json && Array.isArray(json.slides)) slides = json.slides;
      else if (json && Array.isArray(json.data)) slides = json.data;

      setData(slides);
      setImageDiscrepancies(
        Array.isArray(json?.image_discrepancies)
          ? json.image_discrepancies
          : [],
      );
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load slides",
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  // Handle edit functionality
  const flagIdsFrom = (flags) =>
    (flags || [])
      .map((flag) => Number(flag?.id))
      .filter((id) => Number.isFinite(id) && id > 0);

  const flagsKeyFrom = (flags) =>
    flagIdsFrom(flags)
      .sort((a, b) => a - b)
      .join(",");

  // Only the fields the user changed since the modal opened.
  const buildEditPatch = () => {
    const patch = {};
    if ((editFormData?.name ?? "") !== editInitialRef.current.name) {
      patch.name = editFormData.name;
    }
    if (flagsKeyFrom(editFormData?.flags) !== editInitialRef.current.flagsKey) {
      patch.flags = flagIdsFrom(editFormData?.flags);
    }
    return patch;
  };

  // `detail` is an already-fetched GET /slides/:id/ payload; when omitted it
  // is fetched in the background (for programme usage info).
  const openEditModal = async (slide, detail = null) => {
    const initialName = slide.name || "";
    const initialFlags = slide.flags || [];
    editInitialRef.current = {
      name: initialName,
      flagsKey: flagsKeyFrom(initialFlags),
    };
    setEditingSlide(slide);
    setEditFormData({
      name: initialName,
      flags: initialFlags,
    });
    setSlideDetail(detail);
    setEditModalOpen(true);

    // Load flags once (cached) for assignment
    await loadAvailableFlagsIfNeeded();

    if (detail) return;

    // Background fetch for programme usage info
    try {
      const res = await axios.get(`/slides/${slide.id}/`, {
        withCredentials: true,
      });
      setSlideDetail(res.data?.data ?? null);
    } catch {
      // non-critical, ignore
    }
  };

  const handleEditClick = (slide) => openEditModal(slide);

  const handleEditSave = async () => {
    if (!editingSlide) return;

    try {
      // Update the slide with whichever of name/flags changed
      await axios.patch(`/slides/${editingSlide.id}/`, buildEditPatch());

      // Reload slides data to get updated flags
      const response = await axios.get("/slides/");
      const json = response.data;
      let slides = [];
      if (Array.isArray(json)) slides = json;
      else if (json && Array.isArray(json.slides)) slides = json.slides;
      else if (json && Array.isArray(json.data)) slides = json.data;

      setData(slides);

      setEditModalOpen(false);
      setEditingSlide(null);
      setEditFormData({ name: "", flags: [] });
      setEditFlagsWindowOpen(false);

      try {
        if (typeof autoEditAdvanceRef.current === "function")
          autoEditAdvanceRef.current();
      } catch {
        // ignore
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update slide",
      );
    }
  };

  const handleConfirmReviewed = async () => {
    if (!editingSlide) return;
    const slideId = editingSlide.id;

    try {
      setConfirmingReviewed(true);
      setError(null);

      // One request: save any changed name/flags + reviewed.
      await axios.patch(`/slides/${slideId}/`, {
        ...buildEditPatch(),
        reviewed: 1,
      });

      // Reload slides data to reflect updated flags/reviewed.
      const response = await axios.get("/slides/");
      const json = response.data;
      let slides = [];
      if (Array.isArray(json)) slides = json;
      else if (json && Array.isArray(json.slides)) slides = json.slides;
      else if (json && Array.isArray(json.data)) slides = json.data;
      setData(slides);

      setEditModalOpen(false);
      setEditingSlide(null);
      setEditFormData({ name: "", flags: [] });
      setEditFlagsWindowOpen(false);

      try {
        if (typeof autoEditAdvanceRef.current === "function")
          autoEditAdvanceRef.current();
      } catch {
        // ignore
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to confirm slide",
      );
    } finally {
      setConfirmingReviewed(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingSlide(null);
    setEditFormData({ name: "", flags: [] });
    setSlideDetail(null);
    setEditFlagsWindowOpen(false);
    setDeleteConfirmOpen(false);
    setDeletingSlide(false);
    setDeleteSlideError(null);

    try {
      if (typeof autoEditAdvanceRef.current === "function")
        autoEditAdvanceRef.current();
    } catch {
      // ignore
    }
  };

  const handleOpenDeleteConfirm = () => {
    if (!editingSlide) return;
    setDeleteSlideError(null);
    setReplacingSlide(false);
    replaceInitRef.current = false;
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteSlideError(null);
    setReplacingSlide(false);
  };

  useEffect(() => {
    if (!deleteConfirmOpen) return;
    // Requirement: refresh slides when delete modal opens.
    fetchSlides();
  }, [deleteConfirmOpen, fetchSlides]);

  useEffect(() => {
    if (!deleteConfirmOpen) return;
    if (replaceInitRef.current) return;
    if (!Array.isArray(data) || data.length === 0) return;

    const allSlides = data;
    const fileSet = new Set(
      allSlides.map((s) => (s?.file || "").toString()).filter(Boolean),
    );
    const hasAdmin = fileSet.has("Admin.pptx");
    const nextFile = hasAdmin
      ? "Admin.pptx"
      : Array.from(fileSet)[0] || "Admin.pptx";

    const slidesForFile = allSlides
      .filter((s) => (s?.file || "").toString() === nextFile)
      .slice();

    slidesForFile.sort((a, b) => {
      const an = Number(a?.slide_number || 0);
      const bn = Number(b?.slide_number || 0);
      if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn)
        return an - bn;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });

    const holding = slidesForFile.find(
      (s) => (s?.name || "").toString() === "Holding Slide",
    );
    const nextSlide = holding || slidesForFile[0] || null;

    setReplaceFile(nextFile);
    setReplaceSlideId(nextSlide?.id ? String(nextSlide.id) : "");
    replaceInitRef.current = true;
  }, [deleteConfirmOpen, data]);

  const handleDeleteSlide = async ({ replaceId } = {}) => {
    if (!editingSlide) return;

    const slideId = editingSlide.id;
    if (!slideId) return;

    if (replaceId) setReplacingSlide(true);
    else setDeletingSlide(true);
    setDeleteSlideError(null);

    try {
      if (replaceId) {
        await axios.delete(`/slides/${slideId}/`, {
          params: { replace_slide_id: replaceId },
        });
      } else {
        await axios.delete(`/slides/${slideId}/`);
      }

      setData((prev) =>
        Array.isArray(prev) ? prev.filter((s) => s.id !== slideId) : prev,
      );
      setSelectedSlides((prev) => {
        const next = new Set(prev);
        next.delete(slideId);
        return next;
      });
      setSelectedSlideOrder((prev) =>
        Array.isArray(prev) ? prev.filter((id) => id !== slideId) : prev,
      );

      setDeleteConfirmOpen(false);
      handleEditCancel();
      refreshTasks();
    } catch (err) {
      setDeleteSlideError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to delete slide",
      );
    } finally {
      setDeletingSlide(false);
      setReplacingSlide(false);
    }
  };

  // Flags management functions
  const handleOpenFlagsModal = async () => {
    setFlagsModalOpen(true);
    setLoadingFlags(true);
    try {
      const response = await axios.get("/flags/");
      const nextFlags = response.data.data || [];
      setFlags(nextFlags);
      // Keep availableFlags in sync so edit modal always shows all /flags/.
      setAvailableFlags(nextFlags);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load flags",
      );
    } finally {
      setLoadingFlags(false);
    }
  };

  const handleCloseFlagsModal = () => {
    setFlagsModalOpen(false);
    setEditingFlagId(null);
    setEditingFlagName("");
    setCreatingNewFlag(false);
    setNewFlagName("");
  };

  const handleEditFlagClick = (flag) => {
    setEditingFlagId(flag.id);
    setEditingFlagName(flag.name);
  };

  const handleSaveFlagEdit = async (flagId) => {
    try {
      await axios.patch(`/flags/${flagId}/`, {
        name: editingFlagName,
      });

      // Update the flags list
      setFlags((prevFlags) =>
        prevFlags.map((flag) =>
          flag.id === flagId ? { ...flag, name: editingFlagName } : flag,
        ),
      );

      // Keep edit modal options synced.
      setAvailableFlags((prevFlags) =>
        prevFlags.map((flag) =>
          flag.id === flagId ? { ...flag, name: editingFlagName } : flag,
        ),
      );

      setEditingFlagId(null);
      setEditingFlagName("");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to update flag",
      );
    }
  };

  const handleCancelFlagEdit = () => {
    setEditingFlagId(null);
    setEditingFlagName("");
  };

  const handleDeleteFlag = async (flagId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this flag? It will be removed from all slides.",
      )
    ) {
      return;
    }

    try {
      await axios.delete(`/flags/${flagId}/`);

      // Remove from flags list
      setFlags((prevFlags) => prevFlags.filter((flag) => flag.id !== flagId));

      // Remove from available flags
      setAvailableFlags((prevFlags) =>
        prevFlags.filter((flag) => flag.id !== flagId),
      );

      // Remove from currently editing slide if present
      setEditFormData((prev) => ({
        ...prev,
        flags: prev.flags.filter((f) => f.id !== flagId),
      }));

      // Update the main data list
      setData((prevData) =>
        prevData.map((slide) => ({
          ...slide,
          flags: slide.flags ? slide.flags.filter((f) => f.id !== flagId) : [],
        })),
      );
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to delete flag",
      );
    }
  };

  const handleCreateNewFlagClick = () => {
    setCreatingNewFlag(true);
    setNewFlagName("");
  };

  const handleSaveNewFlag = async () => {
    if (!newFlagName.trim()) {
      setError("Flag name is required");
      return;
    }

    try {
      const response = await axios.post("/flags/", {
        name: newFlagName.trim(),
      });

      const created = { id: response.data.id, name: response.data.name };

      // Add the new flag to the list
      setFlags((prevFlags) => [...prevFlags, created]);

      // Keep edit modal options synced.
      setAvailableFlags((prevFlags) => [...prevFlags, created]);

      setCreatingNewFlag(false);
      setNewFlagName("");
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to create flag",
      );
    }
  };

  const handleCancelNewFlag = () => {
    setCreatingNewFlag(false);
    setNewFlagName("");
  };

  // Fixed position for a flags dropdown opened from `anchorEl`, using the shared
  // placement (components/popoverPosition.js): left-aligned with the anchor,
  // shifted to stay on screen and flipped above it near the bottom of the page.
  const computeFixedDropdownPos = (
    anchorEl,
    { width = 360, maxPanelHeight = 420 } = {},
  ) => {
    if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function")
      return null;
    const pos = placePopover(
      anchorEl.getBoundingClientRect(),
      { width, height: maxPanelHeight },
      { gap: 6, align: "start" },
    );
    return {
      top: pos.top,
      left: pos.left,
      width,
      maxHeight: pos.maxHeight ?? maxPanelHeight,
    };
  };

  // Slide flag assignment: dropdown checkbox list (same UX as table list-filter)
  const handleOpenEditFlagsWindow = async (e) => {
    const willOpen = !editFlagsWindowOpen;
    setEditFlagsWindowOpen(willOpen);

    if (willOpen) {
      // Default grouped sections to collapsed whenever the dropdown opens
      setEditFlagsDropdownGroupsOpen({});

      // Capture anchor element for positioning
      try {
        editFlagsAnchorElRef.current = e?.currentTarget || null;
      } catch {
        editFlagsAnchorElRef.current = null;
      }

      // Compute initial position
      setEditFlagsDropdownPos(
        computeFixedDropdownPos(editFlagsAnchorElRef.current),
      );

      // Force refresh so we show all /flags/, including newly created flags
      // that may not appear in /slides/ yet.
      await loadAvailableFlagsIfNeeded({ force: true });
    } else {
      setEditFlagsDropdownPos(null);
    }
  };

  useEffect(() => {
    if (!editFlagsWindowOpen) return;

    const reposition = () => {
      const anchor = editFlagsAnchorElRef.current;
      const next = computeFixedDropdownPos(anchor);
      if (next) setEditFlagsDropdownPos(next);
    };

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    reposition();
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFlagsWindowOpen]);

  useEffect(() => {
    if (!editFlagsWindowOpen) return;

    const onPointerDown = (e) => {
      try {
        const target = e?.target;
        const containerEl = editFlagsDropdownRef.current;
        if (containerEl && target && containerEl.contains(target)) return;
        setEditFlagsWindowOpen(false);
      } catch {
        setEditFlagsWindowOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [editFlagsWindowOpen]);

  const handleToggleEditFlagChecked = (flag, checked) => {
    if (!flag) return;
    setEditFormData((prev) => {
      const current = Array.isArray(prev?.flags) ? prev.flags : [];
      const flagId = flag?.id;
      const exists = current.some((f) => f?.id === flagId);
      const nextFlags = checked
        ? exists
          ? current
          : [...current, { id: flagId, name: flag?.name }]
        : current.filter((f) => f?.id !== flagId);
      return { ...prev, flags: nextFlags };
    });
  };

  // Bulk operations handlers
  const handleSelectSlide = (slideId) => {
    setSelectedSlides((prev) => {
      const newSet = new Set(prev);
      const willSelect = !newSet.has(slideId);
      if (willSelect) newSet.add(slideId);
      else newSet.delete(slideId);

      setSelectedSlideOrder((prevOrder) => {
        if (willSelect) {
          if (prevOrder.includes(slideId)) return prevOrder;
          return [...prevOrder, slideId];
        }
        return prevOrder.filter((id) => id !== slideId);
      });

      return newSet;
    });
  };

  const handleSelectAll = () => {
    const visibleSlideIds = filteredData.map((slide) => slide.id);
    const allVisibleSelected =
      visibleSlideIds.length > 0 &&
      visibleSlideIds.every((id) => selectedSlides.has(id));

    if (allVisibleSelected) {
      // Deselect all visible slides
      setSelectedSlides((prev) => {
        const newSet = new Set(prev);
        visibleSlideIds.forEach((id) => newSet.delete(id));

        setSelectedSlideOrder((prevOrder) =>
          prevOrder.filter((id) => !visibleSlideIds.includes(id)),
        );
        return newSet;
      });
    } else {
      // Select all visible slides
      setSelectedSlides((prev) => {
        const newSet = new Set(prev);
        visibleSlideIds.forEach((id) => newSet.add(id));

        // Append newly-visible IDs in current visible order.
        setSelectedSlideOrder((prevOrder) => {
          const existing = new Set(prevOrder);
          const additions = visibleSlideIds.filter((id) => !existing.has(id));
          if (additions.length === 0) return prevOrder;
          return [...prevOrder, ...additions];
        });
        return newSet;
      });
    }
  };

  const handleOpenBulkFlagsModal = async () => {
    setBulkFlagsModalOpen(true);
    setBulkChanges([]);
    setBulkChangeCounter(0);
    setBulkAddFlagsDropdownOpen(false);
    setBulkRemoveFlagsDropdownOpen(false);
    setBulkAddFlagsDropdownGroupsOpen({});
    setBulkRemoveFlagsDropdownGroupsOpen({});
    setBulkAddFlagsDropdownPos(null);
    setBulkRemoveFlagsDropdownPos(null);

    // Always refresh flags so the bulk modal shows all current /flags/.
    try {
      await loadAvailableFlagsIfNeeded({ force: true });
    } catch {
      // loadAvailableFlagsIfNeeded already sets error on failure
    }
  };

  const handleCloseBulkFlagsModal = () => {
    setBulkFlagsModalOpen(false);
    setBulkChanges([]);
    setBulkChangeCounter(0);
    setBulkAddFlagsDropdownOpen(false);
    setBulkRemoveFlagsDropdownOpen(false);
    setBulkAddFlagsDropdownGroupsOpen({});
    setBulkRemoveFlagsDropdownGroupsOpen({});
    setBulkAddFlagsDropdownPos(null);
    setBulkRemoveFlagsDropdownPos(null);
  };

  const handleAddBulkChange = (flag, action) => {
    setBulkChangeCounter((prevCounter) => {
      setBulkChanges((prev) => [
        ...prev,
        { id: prevCounter, flag_id: flag.id, flag_name: flag.name, action },
      ]);
      return prevCounter + 1;
    });
  };

  useEffect(() => {
    if (!bulkAddFlagsDropdownOpen && !bulkRemoveFlagsDropdownOpen) return;

    const onPointerDown = (e) => {
      try {
        const target = e?.target;
        const addEl = bulkAddFlagsDropdownRef.current;
        const removeEl = bulkRemoveFlagsDropdownRef.current;
        if (addEl && target && addEl.contains(target)) return;
        if (removeEl && target && removeEl.contains(target)) return;
        setBulkAddFlagsDropdownOpen(false);
        setBulkRemoveFlagsDropdownOpen(false);
      } catch {
        setBulkAddFlagsDropdownOpen(false);
        setBulkRemoveFlagsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [bulkAddFlagsDropdownOpen, bulkRemoveFlagsDropdownOpen]);

  const handleRemoveBulkChange = (changeId) => {
    setBulkChanges((prev) => prev.filter((change) => change.id !== changeId));
  };

  const handleApplyBulkChanges = async () => {
    if (bulkChanges.length === 0) return;

    try {
      setError(null);
      const payload = {
        slide_ids: Array.from(selectedSlides || []),
        changes: (bulkChanges || []).map((c) => ({
          flag_id: c.flag_id,
          action: c.action,
        })),
      };
      await axios.patch("/slides/", payload);
      await fetchSlides();
      handleCloseBulkFlagsModal();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to apply bulk changes",
      );
    }
  };

  const searchFilteredData = useMemo(
    () => searchAndRankSlides(data || [], slideSearchTerm),
    [data, slideSearchTerm],
  );

  const handleExportSlidesFromSelectedSlides = async () => {
    // Export should include ALL selected slides, even if the current table search/filter
    // hides some of them. Use a filter-independent selection order.
    const slideLookup = new Map((data || []).map((s) => [s?.id, s]));
    const orderedSelectedSlides = (selectedSlideOrder || [])
      .filter((id) => selectedSlides.has(id))
      .map((id) => slideLookup.get(id))
      .filter(Boolean);

    // Fallback: if we have selected IDs but no order recorded (shouldn't happen),
    // include everything selected from the full dataset.
    const selectedFromAllData = (data || []).filter((slide) =>
      selectedSlides.has(slide.id),
    );
    const finalSelectedSlides = orderedSelectedSlides.length
      ? orderedSelectedSlides
      : selectedFromAllData;

    if (finalSelectedSlides.length === 0) return;

    // API expects: { elements: [{ slide_id: ... }, ...] }
    const payload = {
      elements: finalSelectedSlides.map((slide) => ({
        slide_id: slide.id,
      })),
    };

    try {
      setError(null);
      await axios.post("/jobs/create-slideshow/", payload);
      refreshTasks();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to export slides",
      );
    }
  };

  // Define table columns
  const columns = [
    {
      key: "select",
      width: 32,
      header: (
        <Checkbox
          className="select-checkbox"
          size="lg"
          checked={
            filteredData.length > 0 &&
            filteredData.every((slide) => selectedSlides.has(slide.id))
          }
          onChange={handleSelectAll}
        />
      ),
      type: "custom",
      render: (value, row) => (
        <Checkbox
          className="select-checkbox"
          size="lg"
          checked={selectedSlides.has(row.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectSlide(row.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: "image",
      header: "Image",
      type: "custom",
      width: 128,
      render: (value, row) => {
        if (row.has_image && row.image_url) {
          return (
            <div className="slide-image-container">
              <img
                src={row.image_url}
                alt={row.tag}
                className="slide-image sm-clickable-image sm-clickable-image-zoom-right"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.querySelector(
                    ".sm-slide-image-placeholder",
                  ).style.display = "flex";
                }}
              />
              <div
                className="sm-slide-image-placeholder"
                title="Image failed to load"
                style={{ display: "none" }}
              >
                📷
              </div>
            </div>
          );
        } else {
          return (
            <div className="sm-slide-image-placeholder" title="No image available">
              📷
            </div>
          );
        }
      },
    },
    {
      key: "name",
      header: "Name",
      type: "alphabetical",
      sortable: true,
      width: 181,
      render: (value, row) => {
        const name = row?.name || "—";
        const reviewed = Number(row?.reviewed) === 1;
        if (reviewed) return name;

        return (
          <span className="slide-name-with-review">
            <span className="needs-review-icon" aria-hidden="true">
              ⚠
            </span>
            <span className="needs-review-badge">NEEDS REVIEW</span>
            <span>{name}</span>
          </span>
        );
      },
    },
    {
      key: "file",
      header: "File",
      type: "discrete",
      sortable: true,
      width: 112,
    },
    {
      key: "slide_number",
      header: "Slide #",
      type: "numerical",
      sortable: true,
      width: 32,
    },
    {
      key: "modified",
      header: "Modified",
      type: "date",
      sortable: true,
      width: 112,
      render: (value) => {
        if (!value) return "—";
        try {
          const date = parseDbDate(value);
          if (!date) return value;
          return (
            date.toLocaleDateString("en-GB") +
            " " +
            date.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          );
        } catch {
          return value;
        }
      },
    },
    {
      key: "flags",
      header: "Flags",
      type: "list",
      filterIcon: <IconFlag className="btn-inline-icon" aria-hidden="true" />,
      width: 180,
      getItemLabel: (flag) => flag?.name || "",
      getItemValue: (flag) => flag?.name || "",
      // Table list-filter dropdown should show all flags from /flags/,
      // but disable those not present in current table rows.
      listFilterAllOptions: (availableFlags || [])
        .map((f) => f?.name)
        .filter(Boolean),
    },
  ];

  return (
    <div className="page-layout">
      <PageTitle><span className="page-title-icon" aria-hidden="true">{typeIcons.slides}</span>Slide Manager</PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          {data && (
            <div className="page-header-info">
              {data.length} slide{data.length !== 1 ? "s" : ""}
            </div>
          )}
          <button onClick={handleOpenFlagsModal}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginRight: 6,
              }}
            >
              {typeIcons.flag}
            </span>
            Edit Flags
          </button>
          <button
            onClick={handleOpenBulkFlagsModal}
            disabled={selectedSlides.size === 0}
            style={{ opacity: selectedSlides.size === 0 ? 0.5 : 1 }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginRight: 6,
              }}
            >
              <TbFlagPlus aria-hidden="true" />
            </span>
            Assign Flags ({selectedSlides.size})
          </button>
          <button onClick={handleOpenImportSlidesModal}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginRight: 6,
              }}
            >
              {typeIcons["import"]}
            </span>
            Import slides
          </button>
          <button onClick={handleOpenRestoreSlidesModal}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginRight: 6,
              }}
            >
              {typeIcons["import"]}
            </span>
            Restore slides
          </button>
          <button
            onClick={handleExportSlidesFromSelectedSlides}
            disabled={selectedSlides.size === 0}
            style={{ opacity: selectedSlides.size === 0 ? 0.5 : 1 }}
            title="POSTs { elements: [{ slide_id: ... }, ...] } to /jobs/create-slideshow/"
          >
            Export Slides ({selectedSlides.size})
          </button>
        </div>
      </div>
      <div className="page-content">
        {error && <div className="error-banner">Error: {error}</div>}

        <div className="sm-search-bar">
          <div className="sm-search-input-wrap">
            <input
              autoComplete="off"
              type="text"
              className="sm-search-input"
              placeholder="Search slides..."
              value={slideSearchTerm}
              onChange={(e) => setSlideSearchTerm(e.target.value)}
            />
            {slideSearchTerm && (
              <button
                type="button"
                className="sm-search-clear"
                onClick={() => setSlideSearchTerm("")}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {imageDiscrepancies.length > 0 && (
            <div
              className="sm-image-discrepancy-warning"
              title={imageDiscrepancies
                .map(
                  (d) =>
                    `${d.file}: ${d.db_slides} slide(s) in DB, ${d.actual_images} image(s) on disk`,
                )
                .join("\n")}
            >
              ⚠ Image mismatch:{" "}
              {imageDiscrepancies.map((d, i) => (
                <span key={d.file}>
                  {i > 0 && ", "}
                  <strong>{d.file}</strong> (
                  {d.num_images > 0 ? `+${d.num_images}` : d.num_images} image
                  {Math.abs(d.num_images) !== 1 ? "s" : ""})
                </span>
              ))}
            </div>
          )}
        </div>

        <Table
          data={searchFilteredData}
          columns={columns}
          loading={loading}
          emptyMessage="No slides found."
          defaultSortColumn="file"
          defaultSortDirection="asc"
          onRowClick={handleEditClick}
          onProcessedDataChange={setFilteredData}
          getRowClassName={(row) =>
            Number(row?.reviewed) === 1 ? "" : "needs-review"
          }
          getRowSortPriority={(row) => (Number(row?.reviewed) === 1 ? 1 : 0)}
          fillPage
        />

        {/* Import Slides Modal */}
        <Modal
          isOpen={importSlidesModalOpen}
          onClose={handleCloseImportSlidesModal}
          title={
            importSlidesMode === "restore"
              ? "Restore slides"
              : "Upload powerpoint file"
          }
          boxClassName="modal-wide"
        >
          <div className="form-section">
            {importSlidesMode === "restore" ? (
              <p>
                View and restore slides that have been overwritten or deleted.
              </p>
            ) : (
              <p>Only accepts .pptx files.</p>
            )}

            {!(importSlidesMode === "restore") && (
              <div
                className={`file-upload-area ${importSlidesDragActive ? "drag-active" : ""}`}
                onDragEnter={
                  importSlidesMode === "upload"
                    ? handleImportSlidesDrag
                    : undefined
                }
                onDragLeave={
                  importSlidesMode === "upload"
                    ? handleImportSlidesDrag
                    : undefined
                }
                onDragOver={
                  importSlidesMode === "upload"
                    ? handleImportSlidesDrag
                    : undefined
                }
                onDrop={
                  importSlidesMode === "upload"
                    ? handleImportSlidesDrop
                    : undefined
                }
              >
                {importSlidesMode === "upload" && (
                  <input
                    autoComplete="off"
                    id="pptx-file-input"
                    type="file"
                    accept=".pptx"
                    onChange={handleImportSlidesFileSelect}
                    style={{ display: "none" }}
                  />
                )}

                {!importSlidesFile ? (
                  <div className="upload-prompt">
                    <i className="fas fa-cloud-upload-alt upload-icon"></i>
                    <p>Drag and drop your .pptx file here, or</p>
                    <button
                      className="select-file-btn"
                      onClick={() =>
                        document.getElementById("pptx-file-input").click()
                      }
                      type="button"
                    >
                      Select File
                    </button>
                    <small>.pptx files are accepted</small>
                  </div>
                ) : (
                  <div className="file-selected">
                    <i className="fas fa-file-powerpoint file-icon"></i>
                    <div className="file-info">
                      <p className="file-name">{importSlidesFile.name}</p>
                      <p className="file-size">
                        {formatFileSize(importSlidesFile.size)}
                      </p>
                    </div>
                    <button
                      className="clear-file-btn"
                      onClick={resetImportSlidesState}
                      type="button"
                      title="Clear"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
            )}

            {importSlidesError && (
              <div className="error-message" style={{ marginTop: 12 }}>
                <i className="fas fa-exclamation-triangle"></i>
                {importSlidesError}
              </div>
            )}

            {!(importSlidesMode === "restore") && importSlidesResponse && (
              <div className="processing-results" style={{ marginTop: 12 }}>
                <div className="success-message">
                  <i className="fas fa-info-circle"></i>
                  {importSlidesResponse.message || "Upload complete"}
                </div>
              </div>
            )}

            {importSlidesInventory.length > 0 && (
              <div className="processing-results" style={{ marginTop: 12 }}>
                <h3>Extracted Slides</h3>

                {importSlidesImageWaitUntil &&
                  importSlidesImageWaitMs > 0 &&
                  !importSlidesImagesReady && (
                    <div
                      style={{
                        marginBottom: 12,
                        fontSize: 13,
                        color: "var(--text-color)",
                      }}
                    >
                      Generating images...{" "}
                      {Math.max(
                        0,
                        Math.ceil(
                          (importSlidesImageWaitUntil - Date.now()) / 1000,
                        ),
                      )}
                      s
                    </div>
                  )}

                {importSlidesInventory.map((slide, index) => {
                  const hoverKey = String(slide?.tag || index);
                  const isHoverZoomed = importSlidesHoverZoomKey === hoverKey;
                  const selectedFile = slide?.sort_file || "";
                  const availableNumbers =
                    importSlidesSortNumbersByFile[selectedFile] || [];
                  const hasOp = Boolean(slide?.sort_operation);
                  const hasFile = Boolean(slide?.sort_file);
                  const hasNumber =
                    slide?.sort_number !== null &&
                    slide?.sort_number !== undefined &&
                    slide?.sort_number !== "";
                  const isComplete = hasOp && hasFile && hasNumber;
                  const readyButtonDisabled = !isComplete;

                  const importFieldLabelStyle = {
                    fontSize: 11,
                    lineHeight: "14px",
                    color: "var(--text-color)",
                    opacity: 0.8,
                    marginBottom: 4,
                    whiteSpace: "nowrap",
                  };

                  const slideNo = Number(slide?.slide_number);
                  const canShowImage = Boolean(
                    importSlidesImagesReady &&
                    importSlidesImageBaseName &&
                    Number.isFinite(slideNo) &&
                    slideNo > 0,
                  );
                  const imgTag = canShowImage
                    ? `${importSlidesImageBaseName}_${slideNo}`
                    : "";
                  const slideNoKey =
                    Number.isFinite(slideNo) && slideNo > 0 ? slideNo : null;
                  const retryNonce = slideNoKey
                    ? Number(
                        importSlidesImageRetryNonceBySlideNo?.[slideNoKey] || 0,
                      )
                    : 0;
                  const imgSrc = canShowImage
                    ? `/api/slide-image/${encodeURIComponent(imgTag)}/?r=${encodeURIComponent(String(retryNonce))}`
                    : "";

                  const isGeneratingImages = Boolean(
                    importSlidesImageWaitUntil && !importSlidesImagesReady,
                  );

                  const imgLoaded = Boolean(
                    slideNoKey &&
                    importSlidesImageLoadedBySlideNo?.[slideNoKey],
                  );
                  const showImportedPlaceholder = Boolean(
                    !canShowImage || isGeneratingImages || !imgLoaded,
                  );

                  const selectedNo = Number(slide?.sort_number);
                  const selectedFileStem = (selectedFile || "")
                    .toString()
                    .replace(/\.pptx$/i, "");
                  const canShowSelectedImage = Boolean(
                    selectedFileStem &&
                    Number.isFinite(selectedNo) &&
                    selectedNo > 0,
                  );
                  const selectedImgTag = canShowSelectedImage
                    ? `${selectedFileStem}_${selectedNo}`
                    : "";
                  const selectedImgSrc = canShowSelectedImage
                    ? `/api/slide-image/${encodeURIComponent(selectedImgTag)}/`
                    : "";
                  const selectedPlaceholderText = canShowSelectedImage
                    ? "No image"
                    : "Select target";

                  return (
                    <div
                      key={slide?.tag || index}
                      className={`table-section import-slide-card ${slide?.ready ? "import-slide-card-selected" : ""}`.trim()}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          flexWrap: "nowrap",
                        }}
                      >
                        {/* Imported slide number */}
                        <div
                          style={{
                            flex: "0 0 auto",
                            fontSize: 13,
                            color: "var(--text-color)",
                          }}
                        >
                          <strong>
                            {Number.isFinite(slideNo) ? slideNo : "—"}
                          </strong>
                        </div>

                        {/* Imported slide preview */}
                        <div
                          style={{
                            width: 120,
                            height: 68,
                            border: "1px solid var(--border-color)",
                            borderRadius: 6,
                            overflow: "visible",
                            flex: "0 0 auto",
                            background: "transparent",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-color)",
                            fontSize: 12,
                          }}
                          onMouseEnter={() =>
                            setImportSlidesHoverZoomKey(hoverKey)
                          }
                          onMouseLeave={() =>
                            setImportSlidesHoverZoomKey((prev) =>
                              prev === hoverKey ? null : prev,
                            )
                          }
                        >
                          <span
                            className="import-slide-image-placeholder"
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: showImportedPlaceholder
                                ? "flex"
                                : "none",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 6,
                              textAlign: "center",
                            }}
                          >
                            {isGeneratingImages
                              ? renderCircularProgress(
                                  importSlidesImageProgress,
                                  26,
                                  3,
                                )
                              : "No image"}
                          </span>
                          {canShowImage && (
                            <img
                              src={imgSrc}
                              alt={`Slide ${slideNo}`}
                              className={`sm-clickable-image sm-import-preview-image sm-clickable-image-zoom-right${isHoverZoomed ? " sm-clickable-image-zoomed" : ""}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 6,
                                opacity: imgLoaded ? 1 : 0,
                              }}
                              loading="lazy"
                              decoding="async"
                              onLoad={(e) => {
                                if (!slideNoKey) return;
                                setImportSlidesImageLoadedBySlideNo((prev) => ({
                                  ...(prev || {}),
                                  [slideNoKey]: true,
                                }));

                                // Clear any pending retry timeout for this slide.
                                try {
                                  const pending =
                                    importSlidesImageRetryTimeoutsRef.current?.[
                                      slideNoKey
                                    ];
                                  if (pending) window.clearTimeout(pending);
                                  if (importSlidesImageRetryTimeoutsRef.current)
                                    delete importSlidesImageRetryTimeoutsRef
                                      .current[slideNoKey];
                                } catch {
                                  // ignore
                                }
                              }}
                              onError={(e) => {
                                if (!slideNoKey) return;
                                setImportSlidesImageLoadedBySlideNo((prev) => ({
                                  ...(prev || {}),
                                  [slideNoKey]: false,
                                }));

                                // Requirement: if the countdown is done but the image isn't available yet,
                                // wait 1 second and try again.
                                const currentRetries = Number(
                                  importSlidesImageRetryCountBySlideNo?.[
                                    slideNoKey
                                  ] || 0,
                                );
                                if (currentRetries >= MAX_IMPORT_IMAGE_RETRIES)
                                  return;

                                // Avoid scheduling multiple retries simultaneously for this slide.
                                try {
                                  if (
                                    importSlidesImageRetryTimeoutsRef.current?.[
                                      slideNoKey
                                    ]
                                  )
                                    return;
                                } catch {
                                  // ignore
                                }

                                try {
                                  const timeoutId = window.setTimeout(() => {
                                    try {
                                      if (
                                        importSlidesImageRetryTimeoutsRef.current
                                      )
                                        delete importSlidesImageRetryTimeoutsRef
                                          .current[slideNoKey];
                                    } catch {
                                      // ignore
                                    }
                                    setImportSlidesImageRetryCountBySlideNo(
                                      (prev) => ({
                                        ...(prev || {}),
                                        [slideNoKey]:
                                          Number(prev?.[slideNoKey] || 0) + 1,
                                      }),
                                    );
                                    setImportSlidesImageRetryNonceBySlideNo(
                                      (prev) => ({
                                        ...(prev || {}),
                                        [slideNoKey]:
                                          Number(prev?.[slideNoKey] || 0) + 1,
                                      }),
                                    );
                                  }, 1000);
                                  if (
                                    !importSlidesImageRetryTimeoutsRef.current
                                  )
                                    importSlidesImageRetryTimeoutsRef.current =
                                      {};
                                  importSlidesImageRetryTimeoutsRef.current[
                                    slideNoKey
                                  ] = timeoutId;
                                } catch {
                                  // ignore
                                }
                              }}
                            />
                          )}
                        </div>

                        {/* Imported slide title */}
                        <div style={{ flex: "1 1 auto", minWidth: 240 }}>
                          <div style={importFieldLabelStyle}>Slide Name</div>
                          <input
                            autoComplete="off"
                            type="text"
                            value={slide?.name || ""}
                            onChange={(e) =>
                              handleUpdateImportSlide(index, {
                                name: e.target.value,
                              })
                            }
                            placeholder="(No text found)"
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              border: "1px solid var(--border-color)",
                              borderRadius: 6,
                              background: "var(--bg-color)",
                              color: "var(--text-color)",
                              fontSize: 13,
                            }}
                          />
                        </div>

                        {/* Operation */}
                        <div style={{ flex: "0 0 auto", width: 160 }}>
                          <div style={importFieldLabelStyle}>Operation</div>
                          <select
                            autoComplete="off"
                            value={slide?.sort_operation || ""}
                            onChange={(e) =>
                              handleUpdateImportSlide(index, {
                                sort_operation: e.target.value || null,
                              })
                            }
                            style={{ width: "100%" }}
                          >
                            <option value="">Select...</option>
                            <option value="replace">Overwrite</option>
                            <option value="follow">Add new after</option>
                          </select>
                        </div>
                        <div style={{ flex: "0 0 auto", width: 200 }}>
                          <div style={importFieldLabelStyle}>
                            Destination File
                          </div>
                          <select
                            autoComplete="off"
                            value={slide?.sort_file || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleUpdateImportSlide(index, {
                                sort_file: val || null,
                                sort_number: null,
                              });
                            }}
                            style={{ width: "100%" }}
                          >
                            <option value="">Select...</option>
                            {importSlidesSortFiles.map((f) => (
                              <option key={f} value={f}>
                                {f}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ flex: "0 0 auto", width: 220 }}>
                          <div style={importFieldLabelStyle}>Position</div>
                          <select
                            autoComplete="off"
                            value={slide?.sort_number ?? ""}
                            disabled={!selectedFile}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleUpdateImportSlide(index, {
                                sort_number: val ? Number(val) : null,
                              });
                            }}
                            style={{ width: "100%" }}
                          >
                            <option value="">Select...</option>
                            {availableNumbers.map((n) => {
                              const title =
                                importSlidesSortTitlesByFile?.[selectedFile]?.[
                                  n
                                ];
                              const label = title
                                ? `Slide ${n} - ${title}`
                                : `Slide ${n}`;
                              return (
                                <option key={n} value={n}>
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Selected slide preview */}
                        <div
                          style={{
                            width: 120,
                            height: 68,
                            border: "1px solid var(--border-color)",
                            borderRadius: 6,
                            overflow: "visible",
                            flex: "0 0 auto",
                            background: "transparent",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-color)",
                            fontSize: 12,
                          }}
                          title={
                            canShowSelectedImage
                              ? `Selected: ${selectedFile} slide ${selectedNo}`
                              : "Select a file and slide number"
                          }
                          onMouseEnter={() =>
                            setImportSlidesHoverZoomKey(hoverKey)
                          }
                          onMouseLeave={() =>
                            setImportSlidesHoverZoomKey((prev) =>
                              prev === hoverKey ? null : prev,
                            )
                          }
                        >
                          <span
                            className="import-slide-selected-image-placeholder"
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 6,
                              textAlign: "center",
                            }}
                          >
                            {isGeneratingImages
                              ? renderCircularProgress(
                                  importSlidesImageProgress,
                                  26,
                                  3,
                                )
                              : selectedPlaceholderText}
                          </span>
                          {canShowSelectedImage && (
                            <img
                              src={selectedImgSrc}
                              alt={`Selected slide ${selectedNo}`}
                              className={`sm-clickable-image sm-import-preview-image sm-clickable-image-zoom-left${isHoverZoomed ? " sm-clickable-image-zoomed" : ""}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                              loading="lazy"
                              decoding="async"
                              onLoad={(e) => {
                                try {
                                  const ph =
                                    e.currentTarget?.parentNode?.querySelector?.(
                                      ".import-slide-selected-image-placeholder",
                                    );
                                  if (ph) ph.style.display = "none";
                                } catch {
                                  // ignore
                                }
                              }}
                              onError={(e) => {
                                try {
                                  e.currentTarget.style.display = "none";
                                  const ph =
                                    e.currentTarget?.parentNode?.querySelector?.(
                                      ".import-slide-selected-image-placeholder",
                                    );
                                  if (ph) ph.style.display = "flex";
                                } catch {
                                  // ignore
                                }
                              }}
                            />
                          )}
                        </div>

                        {/* Ready */}
                        <Checkbox
                          className="select-checkbox"
                          size="lg"
                          checked={Boolean(slide?.ready)}
                          disabled={readyButtonDisabled}
                          onChange={() => handleToggleImportSlideReady(index)}
                          title={
                            readyButtonDisabled
                              ? "Complete the row selections first"
                              : slide?.ready
                                ? "Selected"
                                : "Select"
                          }
                        />
                      </div>
                    </div>
                  );
                })}

                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={handleOpenImportSlidesFinalConfirm}
                    className="import-btn"
                    disabled={
                      importSlidesConfirming ||
                      Boolean(replaceConflictMessage) ||
                      importSlidesSelectionStats.selectedCount < 1
                    }
                  >
                    {importSlidesConfirming
                      ? "Importing..."
                      : "Import Selected"}
                  </button>
                </div>

                {replaceConflictMessage && (
                  <div className="error-message" style={{ marginTop: 12 }}>
                    <i className="fas fa-exclamation-triangle"></i>
                    {replaceConflictMessage}
                  </div>
                )}

                {importSlidesConfirmError && (
                  <div className="error-message" style={{ marginTop: 12 }}>
                    <i className="fas fa-exclamation-triangle"></i>
                    {importSlidesConfirmError}
                  </div>
                )}

                {importSlidesConfirmResponse?.message && (
                  <div className="success-message" style={{ marginTop: 12 }}>
                    <i className="fas fa-info-circle"></i>
                    {importSlidesConfirmResponse.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>

        {/* Import Slides Final Confirmation Modal */}
        <Modal
          isOpen={importSlidesFinalConfirmOpen}
          onClose={handleCancelImportSlidesFinalConfirm}
          title="Confirm Import"
        >
          <div className="form-section">
            <p style={{ margin: 0 }}>
              You are about to overwrite{" "}
              {importSlidesSelectionStats.overwriteCount} slide
              {importSlidesSelectionStats.overwriteCount === 1 ? "" : "s"} and
              add {importSlidesSelectionStats.addNewCount} new slide
              {importSlidesSelectionStats.addNewCount === 1 ? "" : "s"}. Are you
              sure?
            </p>

            <div className="action-buttons" style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={handleConfirmImportSlidesFinalConfirm}
                className="import-btn"
                disabled={importSlidesConfirming}
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={handleCancelImportSlidesFinalConfirm}
                disabled={importSlidesConfirming}
                style={{ opacity: importSlidesConfirming ? 0.5 : 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        {/* Importing Modal (only dismissable via "Continue in background") */}
        <Modal
          isOpen={importSlidesApplying}
          onClose={() => {}}
          showCloseButton={false}
          title="Importing..."
        >
          <div style={{ padding: 6, fontSize: 14, color: "var(--text-color)" }}>
            <div>
              Importing
              {importSlidesApplyingCount > 0
                ? ` ${importSlidesApplyingCount} slide${
                    importSlidesApplyingCount === 1 ? "" : "s"
                  }`
                : ""}
              ...
            </div>
            <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9 }}>
              Each imported slide will open for review when the import finishes.
            </div>
            <div className="action-buttons" style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => {
                  // Stop waiting; the backend task keeps running and its
                  // progress/result stays visible in the task panel.
                  importWaitRunRef.current += 1;
                  setImportSlidesApplying(false);
                  setImportSlidesApplyingCount(0);
                }}
              >
                Continue in background
              </button>
            </div>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={editModalOpen}
          onClose={handleEditCancel}
          title={`Edit Slide${editingSlide?.name ? `: ${editingSlide.name}` : ""}`}
        >
          <div className="form-section" style={{ gap: "20px" }}>
            {/* Image Preview Section */}
            <div className="slide-preview-container">
              {editingSlide &&
              editingSlide.has_image &&
              editingSlide.image_url ? (
                <img
                  src={editingSlide.image_url}
                  alt="Slide Preview"
                  className="slide-preview-image"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentNode.querySelector(
                      ".no-image-placeholder",
                    ).style.display = "block";
                  }}
                />
              ) : null}
              <div
                className="no-image-placeholder"
                style={{
                  display:
                    !editingSlide ||
                    !editingSlide.has_image ||
                    !editingSlide.image_url
                      ? "block"
                      : "none",
                }}
              >
                No image available for this slide
              </div>
            </div>

            {/* Edit Form Section */}
            <div className="form-section">
              {editingSlide && (
                <div className="form-field slide-meta-info">
                  {editingSlide.file && (
                    <div>
                      <label>File:</label> <span>{editingSlide.file}</span>
                    </div>
                  )}
                  {editingSlide.slide_number != null && (
                    <div>
                      <label>Slide #:</label>{" "}
                      <span>{editingSlide.slide_number}</span>
                    </div>
                  )}
                  {editingSlide.created && (
                    <div>
                      <label>Created:</label>{" "}
                      <span>
                        {(() => {
                          try {
                            const d = parseDbDate(editingSlide.created);
                            if (!d) return editingSlide.created;
                            return (
                              d.toLocaleDateString("en-GB") +
                              " " +
                              d.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            );
                          } catch {
                            return editingSlide.created;
                          }
                        })()}
                      </span>
                    </div>
                  )}
                  {editingSlide.modified && (
                    <div>
                      <label>Modified:</label>{" "}
                      <span>
                        {(() => {
                          try {
                            const d = parseDbDate(editingSlide.modified);
                            if (!d) return editingSlide.modified;
                            return (
                              d.toLocaleDateString("en-GB") +
                              " " +
                              d.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            );
                          } catch {
                            return editingSlide.modified;
                          }
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="form-field">
                <label>Name:</label>
                <input
                  autoComplete="off"
                  type="text"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Enter slide name"
                />
              </div>

              <div className="form-field">
                <label>Flags:</label>
                {editFormData.flags.length === 0 ? (
                  <div className="empty-message">No flags.</div>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {editFormData.flags.map((flag) => (
                      <div
                        key={flag.id}
                        style={{ color: "var(--text-color)", fontSize: 13 }}
                      >
                        {flag.name}
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className="table-filter-list"
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginTop: 10,
                  }}
                  ref={editFlagsDropdownRef}
                >
                  <button
                    type="button"
                    className="table-filter-button"
                    onClick={handleOpenEditFlagsWindow}
                    title={
                      editFormData.flags.length
                        ? `Selected ${editFormData.flags.length} flag(s)`
                        : "Select flags"
                    }
                  >
                    <IconFlag className="btn-inline-icon" aria-hidden="true" />
                    Edit flags
                    {editFormData.flags.length
                      ? ` (${editFormData.flags.length})`
                      : ""}
                  </button>

                  {editFlagsWindowOpen &&
                    (() => {
                      const selectedIds = new Set(
                        (editFormData?.flags || []).map((f) => f?.id),
                      );
                      const options = Array.isArray(availableFlags)
                        ? availableFlags
                        : [];

                      const groupedMap = new Map();
                      const ungrouped = [];
                      options.forEach((flag) => {
                        const name = flag?.name || "";
                        const { prefix, suffix } = splitPrefixedOption(name);
                        if (prefix && suffix) {
                          if (!groupedMap.has(prefix))
                            groupedMap.set(prefix, []);
                          groupedMap.get(prefix).push({ flag, label: suffix });
                          return;
                        }
                        ungrouped.push({ flag, label: name });
                      });

                      const grouped = Array.from(groupedMap.entries())
                        .map(([prefix, items]) => ({
                          prefix,
                          items: items
                            .slice()
                            .sort((a, b) =>
                              (a.label || "").localeCompare(
                                b.label || "",
                                undefined,
                                { sensitivity: "base" },
                              ),
                            ),
                        }))
                        .sort((a, b) =>
                          (a.prefix || "").localeCompare(
                            b.prefix || "",
                            undefined,
                            { sensitivity: "base" },
                          ),
                        );

                      const multiGroups = grouped.filter(
                        (g) => g.items.length > 1,
                      );
                      const singletons = grouped
                        .filter((g) => g.items.length === 1)
                        .map((g) => ({
                          flag: g.items[0].flag,
                          label: g.items[0].flag?.name || "",
                        }));

                      const finalUngrouped = [...ungrouped, ...singletons]
                        .filter((x) => x?.flag)
                        .sort((a, b) =>
                          (a.label || "").localeCompare(
                            b.label || "",
                            undefined,
                            { sensitivity: "base" },
                          ),
                        );

                      const isGroupOpen = (prefix) =>
                        editFlagsDropdownGroupsOpen?.[prefix] === true;
                      const toggleGroup = (prefix) =>
                        setEditFlagsDropdownGroupsOpen((prev) => ({
                          ...(prev || {}),
                          [prefix]: !(prev?.[prefix] === true),
                        }));

                      return (
                        <div
                          className="table-filter-dropdown"
                          style={{
                            position: "fixed",
                            top: editFlagsDropdownPos?.top ?? 0,
                            left: editFlagsDropdownPos?.left ?? 0,
                            zIndex: 10050,
                            marginTop: 0,
                            width: `${editFlagsDropdownPos?.width ?? 360}px`,
                            maxWidth: `${editFlagsDropdownPos?.width ?? 360}px`,
                            maxHeight: `${editFlagsDropdownPos?.maxHeight ?? 420}px`,
                            minHeight: `${Math.min(220, editFlagsDropdownPos?.maxHeight ?? 420)}px`,
                            overflowY: "auto",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <strong style={{ fontSize: "0.9em" }}>
                              Select flags
                            </strong>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <button
                                type="button"
                                className="table-filter-dropdown-action"
                                onClick={() =>
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    flags: [],
                                  }))
                                }
                                disabled={
                                  (editFormData?.flags || []).length === 0
                                }
                                title="Clear selection"
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                          {options.length === 0 ? (
                            <div style={{ fontSize: "0.9em", opacity: 0.7 }}>
                              No flags available
                            </div>
                          ) : (
                            <div
                              className="table-filter-list-options"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {multiGroups.map((group) => {
                                const open = isGroupOpen(group.prefix);
                                const selectedCount = group.items.reduce(
                                  (acc, item) =>
                                    selectedIds.has(item.flag?.id)
                                      ? acc + 1
                                      : acc,
                                  0,
                                );
                                return (
                                  <div
                                    key={group.prefix}
                                    className="table-filter-group"
                                  >
                                    <button
                                      type="button"
                                      className="table-filter-group-toggle"
                                      onClick={() => toggleGroup(group.prefix)}
                                      title={`Toggle ${group.prefix}`}
                                    >
                                      <span className="table-filter-group-indicator">
                                        {open ? "▾" : "▸"}
                                      </span>
                                      <span className="table-filter-group-title">
                                        {group.prefix}
                                        {selectedCount
                                          ? ` (${selectedCount})`
                                          : ""}
                                      </span>
                                    </button>

                                    {open && (
                                      <div
                                        className="table-filter-group-items"
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "6px",
                                        }}
                                      >
                                        {group.items.map((item) => {
                                          const flag = item.flag;
                                          const checked = selectedIds.has(
                                            flag?.id,
                                          );
                                          return (
                                            <label
                                              key={flag?.id}
                                              className="table-filter-option"
                                            >
                                              <Checkbox
                                                size="sm"
                                                checked={checked}
                                                onChange={() =>
                                                  handleToggleEditFlagChecked(
                                                    flag,
                                                    !checked,
                                                  )
                                                }
                                              />
                                              <span
                                                style={{ fontSize: "0.9em" }}
                                              >
                                                {item.label}
                                              </span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              {finalUngrouped.map((item) => {
                                const flag = item.flag;
                                const checked = selectedIds.has(flag?.id);
                                return (
                                  <label
                                    key={flag?.id ?? item.label}
                                    className="table-filter-option"
                                  >
                                    <Checkbox
                                      size="sm"
                                      checked={checked}
                                      onChange={() =>
                                        handleToggleEditFlagChecked(
                                          flag,
                                          !checked,
                                        )
                                      }
                                    />
                                    <span style={{ fontSize: "0.9em" }}>
                                      {item.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div>

              {editingSlide && Number(editingSlide.reviewed) !== 1 && (
                <div className="slide-review-warning">
                  <div className="slide-review-warning-text">
                    <span
                      className="slide-review-warning-icon"
                      aria-hidden="true"
                    >
                      ⚠
                    </span>
                    <span>
                      This slide has not been reviewed - ensure title and flags
                      are correct then click to confirm:
                    </span>
                  </div>
                  <button
                    onClick={handleConfirmReviewed}
                    disabled={confirmingReviewed}
                  >
                    Confirm Reviewed and Save
                  </button>
                </div>
              )}

              <div className="button-group">
                <button onClick={handleEditSave} className="success-button">
                  Save
                </button>
              </div>

              {editingSlide && (
                <div className="slide-delete-warning">
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={handleOpenDeleteConfirm}
                    disabled={deletingSlide}
                  >
                    Delete
                  </button>
                  <span className="slide-delete-warning-text">
                    Warning - this action is not reversible.
                  </span>
                </div>
              )}
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={deleteConfirmOpen}
          onClose={handleCloseDeleteConfirm}
          title="Confirm delete"
        >
          <div className="form-section">
            <div>
              Are you sure you want to delete slide '{editingSlide?.name || ""}
              '?
            </div>

            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="delete-btn"
                onClick={() => handleDeleteSlide()}
                disabled={deletingSlide || replacingSlide}
              >
                {deletingSlide ? "Deleting..." : "Delete"}
              </button>
            </div>

            {(() => {
              const uses = slideDetail?.programme_uses;
              if (!Array.isArray(uses) || uses.length === 0) return null;
              const typeOrder = [
                "custom",
                "template",
                "module",
                "part",
                "block",
                "archived",
              ];
              const typeLabel = {
                custom: "custom programmes",
                template: "template programmes",
                module: "modules",
                part: "parts",
                block: "blocks",
                archived: "archived",
              };
              const groups = {};
              for (const u of uses) {
                const t = u.type || "archived";
                if (!groups[t]) groups[t] = [];
                groups[t].push(u.name);
              }
              return (
                <div style={{ marginTop: 8 }}>
                  <div>The slide is in use in:</div>
                  {typeOrder
                    .filter((t) => groups[t]?.length > 0)
                    .map((t) => (
                      <div key={t} style={{ marginTop: 6 }}>
                        <strong>
                          {groups[t].length} {typeLabel[t]}
                        </strong>
                        <ul style={{ margin: "2px 0 0 0", paddingLeft: 18 }}>
                          {groups[t].map((name, i) => (
                            <li key={i}>{name}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              );
            })()}

            {deleteSlideError && (
              <div className="error-banner">Error: {deleteSlideError}</div>
            )}

            {(() => {
              const uses = slideDetail?.programme_uses;
              const hasUses = Array.isArray(uses) && uses.length > 0;
              if (!hasUses) return null;

              return (
                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    border: "1px solid var(--border-color)",
                    borderRadius: 6,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    OR delete and replace with...
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        minWidth: 220,
                      }}
                    >
                      <label style={{ fontSize: "0.9em" }}>File</label>
                      <select
                        autoComplete="off"
                        value={replaceFile || ""}
                        onChange={(e) => {
                          const next = e.target.value;
                          setReplaceFile(next);
                          // Reset slide selection to a sensible default in the new file.
                          const slidesForFile = (
                            Array.isArray(data) ? data : []
                          ).filter((s) => (s?.file || "") === next);
                          const holding = slidesForFile.find(
                            (s) => (s?.name || "") === "Holding Slide",
                          );
                          const nextSlide =
                            holding ||
                            slidesForFile.sort(
                              (a, b) =>
                                Number(a?.slide_number || 0) -
                                Number(b?.slide_number || 0),
                            )[0];
                          setReplaceSlideId(
                            nextSlide?.id ? String(nextSlide.id) : "",
                          );
                        }}
                        disabled={deletingSlide || replacingSlide}
                      >
                        {Array.from(
                          new Set(
                            (Array.isArray(data) ? data : [])
                              .map((s) => s?.file)
                              .filter(Boolean),
                          ),
                        )
                          .sort((a, b) => String(a).localeCompare(String(b)))
                          .map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        minWidth: 320,
                      }}
                    >
                      <label style={{ fontSize: "0.9em" }}>Slide</label>
                      <select
                        autoComplete="off"
                        value={replaceSlideId || ""}
                        onChange={(e) => setReplaceSlideId(e.target.value)}
                        disabled={deletingSlide || replacingSlide}
                      >
                        {(Array.isArray(data) ? data : [])
                          .filter(
                            (s) => (s?.file || "") === (replaceFile || ""),
                          )
                          .slice()
                          .sort(
                            (a, b) =>
                              Number(a?.slide_number || 0) -
                              Number(b?.slide_number || 0),
                          )
                          .map((s) => (
                            <option key={s.id} value={String(s.id)}>
                              {`${s.slide_number ?? ""}`.trim()
                                ? `#${s.slide_number} - `
                                : ""}
                              {s.name || ""}
                            </option>
                          ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className="success-button"
                      onClick={() =>
                        handleDeleteSlide({ replaceId: replaceSlideId })
                      }
                      disabled={
                        deletingSlide ||
                        replacingSlide ||
                        !replaceSlideId ||
                        String(replaceSlideId) ===
                          String(editingSlide?.id || "")
                      }
                      title={
                        String(replaceSlideId) ===
                        String(editingSlide?.id || "")
                          ? "Replacement slide must be different"
                          : "Replace slide references and delete"
                      }
                    >
                      {replacingSlide ? "Replacing..." : "Delete and Replace"}
                    </button>
                  </div>
                </div>
              );
            })()}

            <div className="button-group">
              <button
                type="button"
                onClick={handleCloseDeleteConfirm}
                disabled={deletingSlide || replacingSlide}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        {/* Flags Management Modal */}
        <Modal
          isOpen={flagsModalOpen}
          onClose={handleCloseFlagsModal}
          title="Manage Flags"
        >
          <div className="form-section">
            {loadingFlags ? (
              <div className="loading-message">Loading flags...</div>
            ) : flags.length === 0 && !creatingNewFlag ? (
              <div className="empty-message">No flags found</div>
            ) : (
              <div className="flag-list">
                {flags.map((flag) => (
                  <div key={flag.id} className="flag-item">
                    {editingFlagId === flag.id ? (
                      <>
                        <input
                          autoComplete="off"
                          type="text"
                          value={editingFlagName}
                          onChange={(e) => setEditingFlagName(e.target.value)}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleSaveFlagEdit(flag.id);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleSaveFlagEdit(flag.id)}
                          className="flag-save-button"
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelFlagEdit}
                          className="flag-cancel-button"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flag-name">{flag.name}</span>
                        <button
                          onClick={() => handleEditFlagClick(flag)}
                          className="flag-edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteFlag(flag.id)}
                          className="flag-cancel-button"
                          title="Delete flag"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                ))}

                {/* Create New Flag Section */}
                {creatingNewFlag && (
                  <div className="flag-item">
                    <input
                      autoComplete="off"
                      type="text"
                      value={newFlagName}
                      onChange={(e) => setNewFlagName(e.target.value)}
                      placeholder="Enter new flag name"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSaveNewFlag();
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveNewFlag}
                      className="flag-save-button"
                      title="Create"
                    >
                      ✓
                    </button>
                    <button
                      onClick={handleCancelNewFlag}
                      className="flag-cancel-button"
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="button-group">
              {!creatingNewFlag && !loadingFlags && (
                <button
                  onClick={handleCreateNewFlagClick}
                  className="primary-button"
                >
                  Create New Flag
                </button>
              )}
            </div>
          </div>
        </Modal>

        {/* Bulk Flags Assignment Modal */}
        <Modal
          isOpen={bulkFlagsModalOpen}
          onClose={handleCloseBulkFlagsModal}
          title={`Assign Flags to ${selectedSlides.size} Slide${selectedSlides.size !== 1 ? "s" : ""}`}
          boxClassName="modal-tall"
        >
          <div className="form-section">
            {(() => {
              const options = Array.isArray(availableFlags)
                ? availableFlags
                : [];
              const addSelectedIds = new Set(
                (bulkChanges || [])
                  .filter((c) => c?.action === "add")
                  .map((c) => c?.flag_id),
              );
              const removeSelectedIds = new Set(
                (bulkChanges || [])
                  .filter((c) => c?.action === "remove")
                  .map((c) => c?.flag_id),
              );

              const renderGroupedDropdown = ({
                action,
                title,
                selectedIds,
                open,
                setOpen,
                groupsOpen,
                setGroupsOpen,
                containerRef,
                dropdownPos,
                setDropdownPos,
              }) => {
                const groupedMap = new Map();
                const ungrouped = [];
                options.forEach((flag) => {
                  const name = flag?.name || "";
                  const { prefix, suffix } = splitPrefixedOption(name);
                  if (prefix && suffix) {
                    if (!groupedMap.has(prefix)) groupedMap.set(prefix, []);
                    groupedMap.get(prefix).push({ flag, label: suffix });
                    return;
                  }
                  ungrouped.push({ flag, label: name });
                });

                const grouped = Array.from(groupedMap.entries())
                  .map(([prefix, items]) => ({
                    prefix,
                    items: items
                      .slice()
                      .sort((a, b) =>
                        (a.label || "").localeCompare(
                          b.label || "",
                          undefined,
                          { sensitivity: "base" },
                        ),
                      ),
                  }))
                  .sort((a, b) =>
                    (a.prefix || "").localeCompare(b.prefix || "", undefined, {
                      sensitivity: "base",
                    }),
                  );

                const multiGroups = grouped.filter((g) => g.items.length > 1);
                const singletons = grouped
                  .filter((g) => g.items.length === 1)
                  .map((g) => ({
                    flag: g.items[0].flag,
                    label: g.items[0].flag?.name || "",
                  }));

                const finalUngrouped = [...ungrouped, ...singletons]
                  .filter((x) => x?.flag)
                  .sort((a, b) =>
                    (a.label || "").localeCompare(b.label || "", undefined, {
                      sensitivity: "base",
                    }),
                  );

                const isGroupOpen = (prefix) => groupsOpen?.[prefix] === true;
                const toggleGroup = (prefix) =>
                  setGroupsOpen((prev) => ({
                    ...(prev || {}),
                    [prefix]: !(prev?.[prefix] === true),
                  }));

                const selectedCount = selectedIds.size;

                return (
                  <div
                    className="table-filter-list"
                    style={{ position: "relative", display: "inline-block" }}
                    ref={containerRef}
                  >
                    <button
                      type="button"
                      className="table-filter-button"
                      onClick={(e) => {
                        const willOpen = !open;
                        setOpen(willOpen);
                        if (willOpen) {
                          setGroupsOpen({});
                          const pos = computeFixedDropdownPos(e?.currentTarget);
                          if (pos) setDropdownPos({ ...pos, minWidth: pos.width });
                        } else {
                          setDropdownPos(null);
                        }
                      }}
                      title={
                        selectedCount
                          ? `${title}: ${selectedCount} selected`
                          : title
                      }
                    >
                      {action === "remove"
                        ? <IconFlagMinus className="btn-inline-icon" aria-hidden="true" />
                        : <IconFlagPlus className="btn-inline-icon" aria-hidden="true" />}
                      {title}
                      {selectedCount ? ` (${selectedCount})` : ""}
                    </button>

                    {open && (
                      <div
                        className="table-filter-dropdown"
                        style={{
                          position: "fixed",
                          top: dropdownPos?.top ?? 0,
                          left: dropdownPos?.left ?? 0,
                          zIndex: 10050,
                          marginTop: 0,
                          width: `${dropdownPos?.minWidth ?? 360}px`,
                          maxWidth: `${dropdownPos?.minWidth ?? 360}px`,
                          maxHeight: `${dropdownPos?.maxHeight ?? 420}px`,
                          minHeight: `${Math.min(220, dropdownPos?.maxHeight ?? 420)}px`,
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <strong style={{ fontSize: "0.9em" }}>{title}</strong>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <button
                              type="button"
                              className="table-filter-dropdown-action"
                              onClick={() =>
                                setBulkChanges((prev) =>
                                  (prev || []).filter(
                                    (c) => c?.action !== action,
                                  ),
                                )
                              }
                              disabled={selectedCount === 0}
                              title="Clear selection"
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        {options.length === 0 ? (
                          <div style={{ fontSize: "0.9em", opacity: 0.7 }}>
                            No flags available
                          </div>
                        ) : (
                          <div
                            className="table-filter-list-options"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {multiGroups.map((group) => {
                              const openGroup = isGroupOpen(group.prefix);
                              const groupSelectedCount = group.items.reduce(
                                (acc, item) =>
                                  selectedIds.has(item.flag?.id)
                                    ? acc + 1
                                    : acc,
                                0,
                              );
                              return (
                                <div
                                  key={group.prefix}
                                  className="table-filter-group"
                                >
                                  <button
                                    type="button"
                                    className="table-filter-group-toggle"
                                    onClick={() => toggleGroup(group.prefix)}
                                    title={`Toggle ${group.prefix}`}
                                  >
                                    <span className="table-filter-group-indicator">
                                      {openGroup ? "▾" : "▸"}
                                    </span>
                                    <span className="table-filter-group-title">
                                      {group.prefix}
                                      {groupSelectedCount
                                        ? ` (${groupSelectedCount})`
                                        : ""}
                                    </span>
                                  </button>

                                  {openGroup && (
                                    <div
                                      className="table-filter-group-items"
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "6px",
                                      }}
                                    >
                                      {group.items.map((item) => {
                                        const flag = item.flag;
                                        const checked = selectedIds.has(
                                          flag?.id,
                                        );
                                        return (
                                          <label
                                            key={flag?.id}
                                            className="table-filter-option"
                                          >
                                            <Checkbox
                                              size="sm"
                                              checked={checked}
                                              onChange={() => {
                                                const exists = (
                                                  bulkChanges || []
                                                ).some(
                                                  (c) =>
                                                    c?.flag_id === flag?.id &&
                                                    c?.action === action,
                                                );
                                                if (exists) {
                                                  setBulkChanges((prev) =>
                                                    (prev || []).filter(
                                                      (c) =>
                                                        !(
                                                          c?.flag_id ===
                                                            flag?.id &&
                                                          c?.action === action
                                                        ),
                                                    ),
                                                  );
                                                } else {
                                                  handleAddBulkChange(
                                                    flag,
                                                    action,
                                                  );
                                                }
                                              }}
                                            />
                                            <span style={{ fontSize: "0.9em" }}>
                                              {item.label}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {finalUngrouped.map((item) => {
                              const flag = item.flag;
                              const checked = selectedIds.has(flag?.id);
                              return (
                                <label
                                  key={flag?.id ?? item.label}
                                  className="table-filter-option"
                                >
                                  <Checkbox
                                    size="sm"
                                    checked={checked}
                                    onChange={() => {
                                      const exists = (bulkChanges || []).some(
                                        (c) =>
                                          c?.flag_id === flag?.id &&
                                          c?.action === action,
                                      );
                                      if (exists) {
                                        setBulkChanges((prev) =>
                                          (prev || []).filter(
                                            (c) =>
                                              !(
                                                c?.flag_id === flag?.id &&
                                                c?.action === action
                                              ),
                                          ),
                                        );
                                      } else {
                                        handleAddBulkChange(flag, action);
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: "0.9em" }}>
                                    {item.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                  }}
                >
                  {renderGroupedDropdown({
                    action: "add",
                    title: "Add flags",
                    selectedIds: addSelectedIds,
                    open: bulkAddFlagsDropdownOpen,
                    setOpen: (next) => {
                      setBulkAddFlagsDropdownOpen(next);
                      if (next) setBulkRemoveFlagsDropdownOpen(false);
                    },
                    groupsOpen: bulkAddFlagsDropdownGroupsOpen,
                    setGroupsOpen: setBulkAddFlagsDropdownGroupsOpen,
                    containerRef: bulkAddFlagsDropdownRef,
                    dropdownPos: bulkAddFlagsDropdownPos,
                    setDropdownPos: setBulkAddFlagsDropdownPos,
                  })}

                  {renderGroupedDropdown({
                    action: "remove",
                    title: "Remove flags",
                    selectedIds: removeSelectedIds,
                    open: bulkRemoveFlagsDropdownOpen,
                    setOpen: (next) => {
                      setBulkRemoveFlagsDropdownOpen(next);
                      if (next) setBulkAddFlagsDropdownOpen(false);
                    },
                    groupsOpen: bulkRemoveFlagsDropdownGroupsOpen,
                    setGroupsOpen: setBulkRemoveFlagsDropdownGroupsOpen,
                    containerRef: bulkRemoveFlagsDropdownRef,
                    dropdownPos: bulkRemoveFlagsDropdownPos,
                    setDropdownPos: setBulkRemoveFlagsDropdownPos,
                  })}
                </div>
              );
            })()}

            {bulkChanges.length === 0 ? (
              <div className="empty-message">No changes queued</div>
            ) : (
              <div className="flag-list">
                {bulkChanges.map((change) => (
                  <div key={change.id} className="flag-item">
                    <span className="flag-name">
                      {change.action === "add" ? "Adding" : "Removing"}:{" "}
                      {change.flag_name}
                    </span>
                    <button
                      onClick={() => handleRemoveBulkChange(change.id)}
                      className="flag-cancel-button"
                      title="Remove this change"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="button-group" style={{ marginTop: "20px" }}>
              <button
                onClick={handleApplyBulkChanges}
                className="success-button"
                disabled={bulkChanges.length === 0}
              >
                Apply Changes
              </button>
              <button onClick={handleCloseBulkFlagsModal}>Cancel</button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
